import { useState, useEffect } from "react";
import { useCameraContext } from "../components/GameStage";

interface WorldPosition {
  x: number;
  y: number;
}

interface ScreenPosition {
  left: number;
  top: number;
}

export const useWorldToScreen = (worldPos: WorldPosition): ScreenPosition => {
  const { camera, scene } = useCameraContext();
  const [screenPos, setScreenPos] = useState<ScreenPosition>({
    left: 0,
    top: 0,
  });

  useEffect(() => {
    if (!camera || !scene) {
      return;
    }

    const updatePosition = () => {
      // Since Phaser and React are now using the same coordinate system (React's dimensions),
      // we can use a simpler conversion that accounts for the camera but doesn't scale
      const x = worldPos.x - camera.scrollX;
      const y = worldPos.y - camera.scrollY;

      setScreenPos({
        left: x,
        top: y,
      });
    };

    // Initial position calculation
    updatePosition();

    // Subscribe to camera updates
    const onCameraUpdate = () => {
      updatePosition();
    };

    // Listen for post-update events to track camera changes
    scene.events.on("postupdate", onCameraUpdate);

    // Cleanup listeners on unmount
    return () => {
      scene.events.off("postupdate", onCameraUpdate);
    };
  }, [camera, scene, worldPos.x, worldPos.y]);

  return screenPos;
};

// Alternative version that can work with global window.PhaserGame access
export const useWorldToScreenGlobal = (
  worldPos: WorldPosition,
): ScreenPosition => {
  const [screenPos, setScreenPos] = useState<ScreenPosition>({
    left: 0,
    top: 0,
  });

  useEffect(() => {
    const game = (window as any).PhaserGame as Phaser.Game;
    if (!game) return;

    const scene = game.scene.scenes[0]; // Get first scene
    const camera = scene?.cameras?.main;

    if (!camera || !scene) return;

    const updatePosition = () => {
      // Since Phaser and React are now using the same coordinate system (React's dimensions),
      // we can use a simpler conversion that accounts for the camera but doesn't scale
      const x = worldPos.x - camera.scrollX;
      const y = worldPos.y - camera.scrollY;
      setScreenPos({
        left: x,
        top: y,
      });
    };

    // Initial position calculation
    updatePosition();

    // Subscribe to camera updates
    const onCameraUpdate = () => {
      updatePosition();
    };

    scene.events.on("postupdate", onCameraUpdate);

    return () => {
      scene.events.off("postupdate", onCameraUpdate);
    };
  }, [worldPos.x, worldPos.y]);

  return screenPos;
};
